/**
 * Gravity Forms Accessibility
 * ---------------------------
 * Handles focus management and ARIA announcements for form validation errors.
 *
 * On a full page reload after a failed submission Gravity Forms renders
 * `.gform_validation_errors` (the error summary) and marks individual fields
 * with `.gfield_error`. This module:
 *
 * 1. Moves focus to the error summary (or first error field if hidden).
 * 2. Adds `role="alert"` to per-field validation messages so screen readers
 *    announce them.
 * 3. Ensures each error field's input has `aria-describedby` pointing at its
 *    validation message.
 * 4. Hooks into the jQuery `gform_post_render` event to re-run after AJAX
 *    submissions.
 */

function enhanceForm(formEl) {
    if (!formEl) return;

    // ── Per-field: role="alert" + aria-describedby ──────────────────────
    const errorFields = formEl.querySelectorAll('.gfield_error');

    errorFields.forEach((field) => {
        const msg = field.querySelector('.gfield_validation_message');
        if (!msg) return;

        // Ensure the message has an id
        if (!msg.id) {
            const fieldId =
                field.getAttribute('id') || 'gfield_' + Math.random().toString(36).slice(2, 9);
            msg.id = fieldId + '_validation_message';
        }

        // Make it a live region so it is announced
        msg.setAttribute('role', 'alert');

        // Find the primary input / select / textarea inside the field
        const input = field.querySelector(
            'input:not([type="hidden"]):not([type="submit"]), select, textarea'
        );
        if (input) {
            const existing = input.getAttribute('aria-describedby') || '';
            if (!existing.includes(msg.id)) {
                input.setAttribute(
                    'aria-describedby',
                    (existing ? existing + ' ' : '') + msg.id
                );
            }
        }
    });
}

function focusFirstError(formEl) {
    if (!formEl) return;

    // ── Focus management ───────────────────────────────────────────────
    const summary = formEl.querySelector('.gform_validation_errors');

    if (summary && summary.offsetParent !== null) {
        // Summary is visible — focus it (it already has tabindex="-1" from GF)
        if (!summary.hasAttribute('tabindex')) {
            summary.setAttribute('tabindex', '-1');
        }
        summary.focus({ preventScroll: false });
        return;
    }

    // Summary hidden / not present — fall back to the sr-only summary
    if (summary) {
        if (!summary.hasAttribute('tabindex')) {
            summary.setAttribute('tabindex', '-1');
        }
        summary.focus({ preventScroll: false });
        return;
    }

    // No summary at all — focus the first error field's input
    const firstErrorInput = formEl.querySelector(
        '.gfield_error input:not([type="hidden"]):not([type="submit"]), ' +
        '.gfield_error select, .gfield_error textarea'
    );
    if (firstErrorInput) {
        firstErrorInput.focus({ preventScroll: false });
    }
}

function handleValidationErrorLinks() {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('.gform_validation_error_link');
        if (!link) return;

        const targetId = link.getAttribute('href')?.substring(1);
        if (!targetId) return;

        const targetField = document.getElementById(targetId);
        if (!targetField) return;

        e.preventDefault();

        // Scroll the field into view
        targetField.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Focus the first input/select/textarea in the field
        const input = targetField.querySelector(
            'input:not([type="hidden"]):not([type="submit"]), select, textarea'
        );
        if (input) {
            input.focus({ preventScroll: true });
        }
    });
}

/**
 * Initialise accessibility enhancements for every Gravity Form on the page.
 */
export default function initGravityFormsA11y() {
    const forms = document.querySelectorAll('.gform_wrapper');
    if (!forms.length) return;

    // Setup global listener for validation error links once
    if (!window.gformsA11yInitialised) {
        handleValidationErrorLinks();
        window.gformsA11yInitialised = true;
    }

    // Run enhancements on every form present in the DOM
    forms.forEach((wrapper) => {
        const form = wrapper.querySelector('form');
        enhanceForm(form);
    });

    // Focus the first form that actually has errors
    const firstFormWithErrors = document.querySelector(
        '.gform_wrapper .gform_validation_errors'
    );
    if (firstFormWithErrors) {
        const parentForm = firstFormWithErrors.closest('form');
        focusFirstError(parentForm);
    }

    // ── AJAX: listen for Gravity Forms' jQuery event ────────────────────
    if (typeof jQuery !== 'undefined') {
        jQuery(document).off('gform_post_render.a11y').on('gform_post_render.a11y', function (_e, formId) {
            const wrapper = document.getElementById('gform_wrapper_' + formId);
            if (!wrapper) return;

            const form = wrapper.querySelector('form');
            enhanceForm(form);

            // Only move focus if the re-render included validation errors
            if (wrapper.querySelector('.gfield_error')) {
                focusFirstError(form);
            }
        });
    }
}
